import { useEffect, useState } from 'react';
import { Modal, TextInput } from '@carbon/react';
import { defineMessages, useIntl } from 'react-intl';

import sharedMessages from 'Components/utils/sharedMessages';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import { CONTACT_ROLE_MAX_LENGTH } from 'utils/validationUtils';

import styles from './ParishBookCategoryEditModal.module.scss';
import { ParishBookCategoryDto, useUpdateParishBookCategoryMutation } from 'api/parishBookEndpoints';

interface Props {
    open: boolean;
    category: ParishBookCategoryDto | null;
    onClose: () => void;
}

const ParishBookCategoryEditModal = ({ open, category, onClose }: Props) => {
    const intl = useIntl();
    const currentEntityId = useGetCurrentEntityId();
    const [currentName, setCurrentName] = useState('');
    const [updateCategory, { isLoading }] = useUpdateParishBookCategoryMutation();

    useEffect(() => {
        if (category?.name) {
            setCurrentName(category.name);
        }
    }, [category]);

    const onSave = async () => {
        if (!category || !currentEntityId) return;
        await updateCategory({
            entityId: currentEntityId,
            id: category.id,
            data: { id: category.id, name: currentName.trim() },
        });
        onClose();
    };

    return (
        <Modal
            open={open}
            modalHeading={intl.formatMessage(messages.heading, { name: category?.name })}
            primaryButtonText={intl.formatMessage(sharedMessages.save)}
            secondaryButtonText={intl.formatMessage(sharedMessages.cancel)}
            onRequestClose={onClose}
            onRequestSubmit={onSave}
            loadingStatus={isLoading ? 'active' : undefined}
            primaryButtonDisabled={isLoading || currentName.trim().length < 3 || currentName.trim().length > CONTACT_ROLE_MAX_LENGTH}
        >
            <div className={styles.container}>
                <TextInput
                    id="parish-book-category-edit-name"
                    labelText={intl.formatMessage(messages.title)}
                    value={currentName}
                    onChange={(e) => { setCurrentName(e.target.value); }}
                    placeholder={intl.formatMessage(messages.titlePlaceholder)}
                    size="md"
                    type="text"
                    maxLength={CONTACT_ROLE_MAX_LENGTH + 1}
                />
            </div>
        </Modal>
    );
};

const messages = defineMessages({
    heading: {
        id: 'parishBook.categoryModal.heading',
        defaultMessage: 'Zmień dane roli lub funkcji "{name}"',
    },
    title: {
        id: 'parishBook.categoryModal.title',
        defaultMessage: 'Nazwa roli lub funkcji',
    },
    titlePlaceholder: {
        id: 'parishBook.categoryModal.titlePlaceholder',
        defaultMessage: 'Wprowadź nazwę roli lub funkcji',
    },
});

export default ParishBookCategoryEditModal;
